import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Barcode, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@/hooks/use-permissions';
import { useAutoRefresh } from '@/hooks/use-auto-refresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InventoryIssue({ user }) {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions(user);
  
  // Check permission
  if (!hasPermission('inventory_issue')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">You don't have permission to issue inventory.</p>
          <Button onClick={() => navigate('/production-unit')}>Go Back</Button>
        </div>
      </div>
    );
  }
  
  // Work Order states
  const [workOrders, setWorkOrders] = useState([]);
  const [issuedWONumbers, setIssuedWONumbers] = useState([]);
  const [woBarcodeInput, setWoBarcodeInput] = useState('');
  const [selectedWOId, setSelectedWOId] = useState('');
  const [selectedWO, setSelectedWO] = useState(null);
  const [requiredItems, setRequiredItems] = useState([]);
  
  // Item scanning states
  const [selectedItemToIssue, setSelectedItemToIssue] = useState(null);
  const [itemBarcodeInput, setItemBarcodeInput] = useState('');
  const [itemQuantityInput, setItemQuantityInput] = useState('');
  const [issuedItems, setIssuedItems] = useState([]);
  const [barcodeVerified, setBarcodeVerified] = useState(false); // Track if barcode is verified

  // Fetch issued WO numbers first, then fetch work orders
  useEffect(() => {
    fetchIssuedWOs();
  }, []);

  useEffect(() => {
    if (issuedWONumbers.length >= 0) { // Fetch even if empty array
      fetchWorkOrders();
    }
  }, [issuedWONumbers]);

  const fetchIssuedWOs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/inventory/issue-transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Extract unique WO numbers that have been issued against
      const issuedWOs = [...new Set(response.data.map(tx => tx.wo_number).filter(wo => wo))];
      console.log('Already issued WO numbers:', issuedWOs);
      setIssuedWONumbers(issuedWOs);
    } catch (error) {
      console.error('Error fetching issued WOs:', error);
    }
  };

  // Auto-refresh every 5 seconds to update issued WOs list
  useAutoRefresh(fetchIssuedWOs, 5, false);

  const fetchWorkOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/work-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter only PENDING and IN PROGRESS work orders that have NOT been issued against yet
      const activeWOs = response.data.filter(wo => 
        (wo.status === 'PENDING' || wo.status === 'IN PROGRESS') && !issuedWONumbers.includes(wo.wo_number)
      );
      
      console.log('Available WOs for issuing:', activeWOs.length);
      setWorkOrders(activeWOs);
    } catch (error) {
      console.error('Error fetching work orders:', error);
      toast.error('Failed to fetch work orders');
    }
  };

  const handleWOSelect = async (woId) => {
    setSelectedWOId(woId);
    
    if (!woId) {
      setSelectedWO(null);
      setRequiredItems([]);
      setIssuedItems([]);
      setSelectedItemToIssue(null);
      return;
    }
    
    // Fetch full work order details with items/article_data
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/work-orders/${woId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const wo = response.data;
      console.log('Fetched full WO:', wo);
      
      // Reconstruct items from article_data (fabrics, trims, packaging)
      const items = [];
      
      if (wo.article_data) {
        // Add fabrics
        if (wo.article_data.fabrics) {
          wo.article_data.fabrics.forEach(f => {
            items.push({
              type: 'FABRIC',
              code: f.fabric_code,
              color_code: f.color_code,
              color_name: f.color_name,
              description: f.fabric_name || '',
              consumption: f.consumption || 0,
              quantity: f.consumption || 0,
              unit: 'meters'
            });
          });
        }
        
        // Add trims
        if (wo.article_data.trims) {
          wo.article_data.trims.forEach(t => {
            // Trims might use 'quantity' or 'consumption' field
            const trimQty = t.quantity || t.consumption || 0;
            items.push({
              type: 'TRIMS',
              code: t.trim_code,
              color_code: t.color_code,
              color_name: t.color_name,
              description: t.trim_name || '',
              consumption: trimQty,
              quantity: trimQty,
              unit: 'units'
            });
          });
        }
        
        // Add packaging
        if (wo.article_data.packaging) {
          wo.article_data.packaging.forEach(p => {
            // Packaging might use 'quantity' or 'consumption' field
            const pkgQty = p.quantity || p.consumption || 0;
            items.push({
              type: 'PACKAGING',
              code: p.packaging_code,
              color_code: p.color_code || '',
              color_name: p.color_name || '',
              description: p.packaging_name || '',
              consumption: pkgQty,
              quantity: pkgQty,
              unit: 'units'
            });
          });
        }
      }
      
      console.log('Reconstructed items:', items);
      console.log('Items count:', items.length);
      
      setSelectedWO(wo);
      setRequiredItems(items);
      setIssuedItems([]);
      setSelectedItemToIssue(null);
      setWoBarcodeInput('');
      toast.success(`WO ${wo.wo_number} loaded with ${items.length} required items!`);
    } catch (error) {
      console.error('Error fetching work order details:', error);
      toast.error('Failed to load work order details');
    }
  };

  const handleWOBarcodeScan = async (e) => {
    const value = e.target.value;
    setWoBarcodeInput(value);
    
    // Auto-search when barcode has at least 2 digits
    const numericValue = value.replace(/\D/g, '');
    if (numericValue.length >= 2) {
      searchByWOBarcode(value);
    }
  };

  const searchByWOBarcode = async (barcode) => {
    if (!barcode || barcode.trim() === '') {
      toast.error('Please enter a WO barcode');
      return;
    }

    // Extract numeric value from barcode
    const scannedNumber = parseInt(barcode.replace(/\D/g, ''), 10);
    
    if (isNaN(scannedNumber)) {
      toast.error('Invalid barcode format');
      return;
    }
    
    // Find WO by comparing numeric values
    const wo = workOrders.find(w => {
      const woNumber = parseInt(w.wo_number.replace(/\D/g, ''), 10);
      return woNumber === scannedNumber && (w.status === 'PENDING' || w.status === 'IN PROGRESS');
    });
    
    // Check if this WO has already been issued against
    if (wo && issuedWONumbers.includes(wo.wo_number)) {
      toast.error(`Work Order ${wo.wo_number} has already been issued against. Each WO can only be used once.`);
      setWoBarcodeInput('');
      return;
    }
    
    if (wo) {
      // Fetch full work order details with items/article_data
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/work-orders/${wo.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const fullWO = response.data;
        console.log('Fetched full WO via barcode:', fullWO);
        
        // Reconstruct items from article_data (same logic as handleWOSelect)
        const items = [];
        
        if (fullWO.article_data) {
          // Add fabrics
          if (fullWO.article_data.fabrics) {
            fullWO.article_data.fabrics.forEach(f => {
              items.push({
                type: 'FABRIC',
                code: f.fabric_code,
                color_code: f.color_code,
                color_name: f.color_name,
                description: f.fabric_name || '',
                consumption: f.consumption || 0,
                quantity: f.consumption || 0,
                unit: 'meters'
              });
            });
          }
          
          // Add trims
          if (fullWO.article_data.trims) {
            fullWO.article_data.trims.forEach(t => {
              // Trims might use 'quantity' or 'consumption' field
              const trimQty = t.quantity || t.consumption || 0;
              items.push({
                type: 'TRIMS',
                code: t.trim_code,
                color_code: t.color_code,
                color_name: t.color_name,
                description: t.trim_name || '',
                consumption: trimQty,
                quantity: trimQty,
                unit: 'units'
              });
            });
          }
          
          // Add packaging
          if (fullWO.article_data.packaging) {
            fullWO.article_data.packaging.forEach(p => {
              // Packaging might use 'quantity' or 'consumption' field
              const pkgQty = p.quantity || p.consumption || 0;
              items.push({
                type: 'PACKAGING',
                code: p.packaging_code,
                color_code: p.color_code || '',
                color_name: p.color_name || '',
                description: p.packaging_name || '',
                consumption: pkgQty,
                quantity: pkgQty,
                unit: 'units'
              });
            });
          }
        }
        
        console.log('Reconstructed items from barcode scan:', items);
        console.log('Items count:', items.length);
        
        setSelectedWO(fullWO);
        setSelectedWOId(fullWO.id);
        setRequiredItems(items);
        setIssuedItems([]);
        setSelectedItemToIssue(null);
        toast.success(`WO ${fullWO.wo_number} loaded with ${items.length} required items!`);
      } catch (error) {
        console.error('Error fetching work order details via barcode:', error);
        toast.error('Failed to load work order details');
      }
    } else {
      toast.error('No PENDING/IN PROGRESS WO found with this barcode');
    }
  };

  const handleItemClick = (item) => {
    // Check if already issued
    const alreadyIssued = issuedItems.find(i => 
      i.code === item.code && 
      i.color_code === item.color_code &&
      i.type === item.type
    );
    
    if (alreadyIssued) {
      toast.warning('This item has already been issued');
      return;
    }
    
    setSelectedItemToIssue(item);
    setItemBarcodeInput('');
    setItemQuantityInput('');
    setBarcodeVerified(false); // Reset verification state
  };

  const handleScanItemBarcode = () => {
    if (!itemBarcodeInput || !itemBarcodeInput.trim()) {
      toast.error('Please scan or enter the item barcode');
      return;
    }

    console.log('=== BARCODE VERIFICATION ===');
    console.log('Selected item:', selectedItemToIssue);
    console.log('Scanned barcode:', itemBarcodeInput);

    // Build expected barcode: code-color_code (e.g., "LN-FAB-001-RED")
    const code = selectedItemToIssue.code || '';
    const colorCode = selectedItemToIssue.color_code || '';
    
    console.log('Item code:', code);
    console.log('Item color_code:', colorCode);
    
    // Expected barcode format
    const expectedBarcode = `${code}-${colorCode}`.toUpperCase();
    const scannedBarcode = itemBarcodeInput.trim().toUpperCase();
    
    console.log('Expected:', expectedBarcode);
    console.log('Scanned:', scannedBarcode);
    console.log('Match:', scannedBarcode === expectedBarcode);

    // Verify if scanned barcode matches
    if (scannedBarcode === expectedBarcode) {
      setBarcodeVerified(true);
      toast.success('✓ Barcode Verified! Please enter quantity to issue.', {
        duration: 3000
      });
      console.log('✅ Barcode verified successfully');
    } else {
      setBarcodeVerified(false);
      toast.error(`Incorrect item scanned! Expected: ${expectedBarcode}`, {
        duration: 5000
      });
      console.log('❌ Barcode mismatch');
    }
  };

  const handleConfirmIssue = async () => {
    console.log('=== CONFIRM ISSUE ===');
    console.log('Barcode verified:', barcodeVerified);
    console.log('Item quantity input:', itemQuantityInput);
    console.log('Item quantity input type:', typeof itemQuantityInput);
    
    if (!barcodeVerified) {
      console.log('❌ Failed: Barcode not verified');
      toast.error('Please verify the barcode first');
      return;
    }
    
    if (!itemQuantityInput || parseFloat(itemQuantityInput) <= 0) {
      console.log('❌ Failed: Invalid quantity input');
      toast.error('Please enter a valid quantity');
      return;
    }

    const quantity = parseFloat(itemQuantityInput);
    const required = parseFloat(selectedItemToIssue.consumption || selectedItemToIssue.quantity || 0);

    console.log('Parsed quantity:', quantity, 'type:', typeof quantity);
    console.log('Required quantity:', required, 'type:', typeof required);
    console.log('Are they equal?', quantity === required);
    console.log('Difference:', Math.abs(quantity - required));

    // MUST BE EXACT MATCH - no more, no less
    // Using Math.abs to handle floating point precision issues
    if (Math.abs(quantity - required) > 0.001) {
      console.log('❌ Failed: Quantity mismatch');
      toast.error(`Quantity must be exactly ${required} ${selectedItemToIssue.unit}. Cannot be more or less.`);
      return;
    }
    
    console.log('✅ Validation passed! Proceeding to check stock...');

    // Check stock availability
    try {
      const token = localStorage.getItem('token');
      console.log('Checking stock availability...');
      console.log('Token exists:', !!token);
      
      const stockResponse = await axios.get(`${API}/inventory/stock`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Stock response received, items count:', stockResponse.data.length);
      console.log('Looking for item with:', {
        code: selectedItemToIssue.code,
        color_code: selectedItemToIssue.color_code,
        type: selectedItemToIssue.type
      });

      const raw_good_type = selectedItemToIssue.type === 'FABRIC' ? 'FABRIC LN' : 
                            selectedItemToIssue.type === 'TRIMS' ? 'TRIMS AND ACCESSORIES LN-Z' : 
                            'PACKAGING LN-Y';
      
      console.log('Searching for raw_good_type:', raw_good_type);

      const stockItem = stockResponse.data.find(s => 
        s.code === selectedItemToIssue.code && 
        s.color_code === selectedItemToIssue.color_code &&
        s.raw_good_type === raw_good_type
      );

      console.log('Stock item found:', stockItem);

      if (!stockItem) {
        console.log('❌ Stock item not found!');
        toast.error('Item not found in stock');
        return;
      }

      console.log('Stock quantity on hand:', stockItem.quantity_on_hand);
      console.log('Quantity to issue:', quantity);

      if (stockItem.quantity_on_hand < quantity) {
        console.log('❌ Insufficient stock!');
        toast.error(`Insufficient stock! Available: ${stockItem.quantity_on_hand}`);
        return;
      }

      console.log('✅ Stock check passed! Adding to issued items...');

      // Add to issued items
      setIssuedItems(prev => {
        const newList = [...prev, {
          ...selectedItemToIssue,
          issued_quantity: quantity,
          stock_id: stockItem.id
        }];
        console.log('New issued items list:', newList);
        return newList;
      });

      toast.success(`${quantity} ${selectedItemToIssue.unit} of ${selectedItemToIssue.code} added to issue list`);
      
      console.log('✅ Item added successfully! Resetting dialog...');
      
      // Reset for next item
      setSelectedItemToIssue(null);
      setItemBarcodeInput('');
      setItemQuantityInput('');
      setBarcodeVerified(false);
      
      console.log('✅ Dialog reset complete!');
    } catch (error) {
      console.error('Error checking stock:', error);
      toast.error('Failed to verify stock availability');
    }
  };

  const handleIssue = async () => {
    console.log('=== ISSUE MATERIALS BUTTON CLICKED ===');
    console.log('Selected WO:', selectedWO);
    console.log('Issued items:', issuedItems);
    console.log('Required items:', requiredItems);
    
    if (!selectedWO) {
      toast.error('No Work Order selected');
      return;
    }

    if (issuedItems.length === 0) {
      toast.error('Please issue at least one item');
      return;
    }

    // Check if ALL required items are fulfilled
    const allFulfilled = requiredItems.every(reqItem => 
      issuedItems.some(f => 
        f.code === reqItem.code && 
        f.color_code === reqItem.color_code &&
        f.type === reqItem.type
      )
    );

    console.log('All items fulfilled?', allFulfilled);

    if (!allFulfilled) {
      toast.error('All required items must be issued before submitting!');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      console.log('Starting to issue items to backend...');
      
      // Issue each item
      for (const item of issuedItems) {
        const raw_good_type = item.type === 'FABRIC' ? 'FABRIC LN' : 
                             item.type === 'TRIMS' ? 'TRIMS AND ACCESSORIES LN-Z' : 
                             'PACKAGING LN-Y';
        
        console.log('Issuing item:', item);
        
        await axios.post(
          `${API}/inventory/issue`,
          {
            wo_number: selectedWO.wo_number,
            raw_good_type: raw_good_type,
            code: item.code,
            color_code: item.color_code,
            color_name: item.color_name,
            quantity_issued: item.issued_quantity,
            remarks: `Issued against WO: ${selectedWO.wo_number}`
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      console.log('All items issued to backend successfully');

      // Update WO status to IN PROGRESS if it was PENDING
      if (selectedWO.status === 'PENDING') {
        console.log('Updating WO status to IN PROGRESS...');
        await axios.patch(
          `${API}/work-orders/${selectedWO.id}/status`,
          { status: 'IN PROGRESS' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('WO status updated');
      }

      toast.success('✅ All materials issued successfully!');
      
      // Reset form
      setSelectedWO(null);
      setRequiredItems([]);
      setIssuedItems([]);
      setWoBarcodeInput('');
      setItemBarcodeInput('');
      setSelectedItemToIssue(null); // Close the dialog
      setItemQuantityInput('');
      setBarcodeVerified(false);
      
      // Refresh the issued WOs list and available WOs
      fetchIssuedWOs();
      
      console.log('Form reset complete');
    } catch (error) {
      console.error('Error issuing materials:', error);
      console.error('Error details:', error.response);
      toast.error(error.response?.data?.detail || 'Failed to issue materials');
    }
  };

  const clearWO = () => {
    setSelectedWO(null);
    setSelectedWOId('');
    setRequiredItems([]);
    setIssuedItems([]);
    setWoBarcodeInput('');
    setItemBarcodeInput('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-md border-b mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/inventory')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Issue Materials
              </h1>
              <p className="text-sm text-gray-600">Issue materials against internal work orders</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {/* Search Work Order */}
        <div className="bg-white border-2 border-black p-6 mb-6">
          <h2 className="text-lg font-bold mb-4 uppercase tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Search Work Order
          </h2>
          
          <div className="space-y-6">
            {/* Option 1: Scan WO Barcode */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">
                1. Scan WO Barcode
              </Label>
              <div className="flex gap-2 mt-1">
                <div className="relative flex-1">
                  <Barcode className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Scan WO barcode here"
                    value={woBarcodeInput}
                    onChange={handleWOBarcodeScan}
                    className="pl-12"
                    autoFocus
                    disabled={selectedWO !== null}
                  />
                </div>
                <Button 
                  onClick={() => searchByWOBarcode(woBarcodeInput)}
                  className="bg-black hover:bg-gray-800"
                  disabled={!woBarcodeInput || selectedWO !== null}
                >
                  Search
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Scan WO barcode to automatically load the work order</p>
            </div>

            <div className="border-t border-gray-300"></div>

            {/* Option 2: Select from Active Work Orders */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">
                2. Select from Active Work Orders
              </Label>
              <SearchableSelect
                value={selectedWOId}
                onChange={handleWOSelect}
                options={[
                  { value: '', label: 'Select work order...' },
                  ...workOrders.map(wo => ({
                    value: wo.id,
                    label: `${wo.wo_number} - ${wo.article_code} (${wo.status})`
                  }))
                ]}
                placeholder="Select from active work orders..."
                className="mt-1"
                disabled={selectedWO !== null}
              />
              <p className="text-xs text-gray-500 mt-1">
                View active WOs ({workOrders.length} available)
              </p>
            </div>

            {/* Clear Search Button */}
            {selectedWO && (
              <div className="flex justify-end pt-2 border-t border-gray-300">
                <Button onClick={clearWO} variant="outline" size="sm">
                  Clear Search
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* WO Details */}
        {selectedWO && (
          <>
            <div className="bg-white border-2 border-black p-6 mb-6">
              <h2 className="text-lg font-bold mb-4 uppercase tracking-tight">WO Details</h2>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <p className="text-xs font-semibold text-gray-500">WO NUMBER</p>
                  <p className="text-sm font-bold">{selectedWO.wo_number}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500">ARTICLE CODE</p>
                  <p className="text-sm">{selectedWO.article_code}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500">STATUS</p>
                  <p className="text-sm">{selectedWO.status}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500">DESCRIPTION</p>
                  <p className="text-sm">{selectedWO.description}</p>
                </div>
              </div>
            </div>

            {/* Required Items and Scanning */}
            <div className="bg-white border-2 border-black p-6 mb-6">
              <h2 className="text-lg font-bold mb-4 uppercase tracking-tight">
                Required Materials
              </h2>
              
              <p className="text-sm text-gray-600 mb-4">Click on each item below to scan barcode and enter quantity to issue</p>

              {/* Fulfillment Status */}
              <div className="mb-6 pb-6 border-b-2 border-gray-200">
                <p className="text-sm font-bold mb-3 uppercase tracking-tight">Issuance Status</p>
                <div className="flex gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-500 border border-black"></div>
                    <span className="text-xs font-semibold">Issued ({issuedItems.length})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-gray-300 border border-black"></div>
                    <span className="text-xs font-semibold">Pending ({requiredItems.length - issuedItems.length})</span>
                  </div>
                </div>
              </div>

              {/* Required Items List - CLICKABLE */}
              <div>
                <h3 className="text-sm font-bold mb-3 uppercase tracking-tight">
                  Required Items ({requiredItems.length}) - Click to Issue
                </h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {requiredItems.map((item, idx) => {
                    const isIssued = issuedItems.some(f => 
                      f.code === item.code && 
                      f.color_code === item.color_code &&
                      f.type === item.type
                    );
                    
                    return (
                      <div 
                        key={idx} 
                        onClick={() => !isIssued && handleItemClick(item)}
                        className={`p-3 border-2 cursor-pointer transition-all ${
                          isIssued 
                            ? 'bg-green-50 border-green-500 cursor-not-allowed' 
                            : 'bg-white border-gray-300 hover:border-black hover:shadow-md'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1 grid grid-cols-4 gap-4">
                            <div>
                              <p className="text-xs font-semibold text-gray-500">TYPE</p>
                              <p className="text-sm font-bold">{item.type}</p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-gray-500">CODE</p>
                              <p className="text-sm font-bold">{item.code}</p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-gray-500">COLOR</p>
                              <p className="text-sm">{item.color_code} - {item.color_name}</p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-gray-500">QUANTITY</p>
                              <p className="text-sm font-bold">{item.consumption || item.quantity}</p>
                            </div>
                          </div>
                          {isIssued && (
                            <CheckCircle className="w-5 h-5 text-green-600 ml-4" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Issue Button */}
              <div className="mt-6 pt-6 border-t-2 border-gray-200">
                <Button 
                  onClick={handleIssue} 
                  className="w-full bg-black hover:bg-gray-800 text-white disabled:bg-gray-400 disabled:cursor-not-allowed"
                  disabled={issuedItems.length !== requiredItems.length}
                >
                  Issue Materials ({issuedItems.length}/{requiredItems.length})
                  {issuedItems.length === requiredItems.length && ' ✓ All Items Ready'}
                </Button>
                {issuedItems.length !== requiredItems.length && (
                  <p className="text-xs text-red-600 text-center mt-2">
                    All required items must be issued before submitting
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </main>

      {/* Item Issue Dialog */}
      <Dialog open={!!selectedItemToIssue} onOpenChange={() => {
        setSelectedItemToIssue(null);
        setBarcodeVerified(false);
      }}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader className="border-b-2 border-black pb-4">
            <DialogTitle className="text-xl font-bold tracking-tight uppercase">
              Issue Item
            </DialogTitle>
          </DialogHeader>
          
          {selectedItemToIssue && (
            <div className="py-4 space-y-6">
              {/* Item Details */}
              <div className="p-4 bg-gray-50 border-2 border-gray-300">
                <p className="text-xs font-semibold text-gray-500 mb-2">ITEM DETAILS</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="font-semibold">Type:</span> {selectedItemToIssue.type}
                  </div>
                  <div>
                    <span className="font-semibold">Code:</span> {selectedItemToIssue.code}
                  </div>
                  <div>
                    <span className="font-semibold">Color:</span> {selectedItemToIssue.color_code}
                  </div>
                  <div>
                    <span className="font-semibold">Required:</span> {selectedItemToIssue.consumption || selectedItemToIssue.quantity} {selectedItemToIssue.unit}
                  </div>
                </div>
              </div>

              {/* Step 1: Barcode Scan - Always Visible */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2 block">
                  Step 1: Scan Item Barcode
                </Label>
                <p className="text-xs text-gray-600 mb-2">
                  Expected: <span className="font-bold text-black">{selectedItemToIssue.code}-{selectedItemToIssue.color_code}</span>
                </p>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Barcode className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Scan barcode here"
                      value={itemBarcodeInput}
                      onChange={(e) => setItemBarcodeInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleScanItemBarcode();
                        }
                      }}
                      className={`pl-12 border-2 ${barcodeVerified ? 'border-green-500 bg-green-50' : 'border-black'}`}
                      disabled={barcodeVerified}
                      autoFocus
                    />
                  </div>
                  <Button 
                    onClick={handleScanItemBarcode} 
                    className="bg-black hover:bg-gray-800"
                    disabled={barcodeVerified || !itemBarcodeInput}
                  >
                    {barcodeVerified ? '✓ Verified' : 'Verify'}
                  </Button>
                </div>
                {barcodeVerified && (
                  <p className="text-xs text-green-600 font-semibold mt-2">
                    ✓ Barcode verified successfully!
                  </p>
                )}
              </div>

              {/* Step 2: Quantity Input - Only Show After Verification */}
              {barcodeVerified && (
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2 block">
                    Step 2: Enter Quantity to Issue
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Enter quantity"
                    value={itemQuantityInput}
                    onChange={(e) => setItemQuantityInput(e.target.value)}
                    className="border-2 border-black"
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Required: {selectedItemToIssue.consumption || selectedItemToIssue.quantity} {selectedItemToIssue.unit} (exact amount only)
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t-2 border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedItemToIssue(null);
                    setBarcodeVerified(false);
                    setItemBarcodeInput('');
                    setItemQuantityInput('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmIssue}
                  className="flex-1 bg-black hover:bg-gray-800"
                  disabled={!barcodeVerified || !itemQuantityInput}
                >
                  Confirm Issue
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
